import { useState, useEffect, useCallback } from 'react'
import JSZip from 'jszip'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Rocket, Trash2, AlertTriangle, CheckCircle2, Clock, XCircle, Download, Shield, Terminal, ChevronDown, ChevronUp, Wrench, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import { iacApi, type IaCCloud, type IaCDeployment, type IaCDeployTarget } from '../api'
import { deployScript, destroyScript } from '../deployScripts'
import { formatDateTime } from '@/lib/dateUtils'

interface DeploymentPanelProps {
    projectId: number | null
    cloudPlatform: string
    files: Array<{ filename: string; code: string }>
    mainId: string
    scenario?: number
    hierarchyContext?: { serviceName?: string; objectCount?: number; versionCount?: number; hierarchyData?: any[] } | null
    onAskAgentToFix?: (errorMessage: string) => void
    verified?: boolean
    /** Reports the current deployment status up to the parent (e.g. to gate Clara). */
    onStatusChange?: (status: string) => void
    /** Fired when the user downloads the Terraform project. */
    onDownloaded?: () => void
    /** True while the agent is still generating/verifying — deploy & download stay disabled. */
    busy?: boolean
}

function StatusBadge({ status }: { status: string }) {
    const config: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle2; color?: string }> = {
        not_deployed: { label: 'Not Deployed', variant: 'secondary', icon: Clock },
        pending: { label: 'Pending', variant: 'outline', icon: Clock },
        planning: { label: 'Initializing...', variant: 'outline', icon: Loader2, color: 'text-blue-500' },
        planned: { label: 'Plan Ready', variant: 'default', icon: CheckCircle2, color: 'text-green-500' },
        deploying: { label: 'Deploying...', variant: 'outline', icon: Rocket, color: 'text-blue-500' },
        deployed: { label: 'Deployed', variant: 'default', icon: CheckCircle2, color: 'text-green-500' },
        destroying: { label: 'Destroying...', variant: 'outline', icon: Trash2, color: 'text-orange-500' },
        destroyed: { label: 'Destroyed', variant: 'secondary', icon: XCircle },
        failed: { label: 'Failed', variant: 'destructive', icon: XCircle },
    }
    const c = config[status] || config.not_deployed
    const Icon = c.icon
    const isAnimated = ['planning', 'deploying', 'destroying', 'pending'].includes(status)
    return (
        <Badge variant={c.variant} className="gap-1">
            <Icon className={`h-3 w-3 ${isAnimated ? 'animate-spin' : ''} ${c.color || ''}`} />{c.label}
        </Badge>
    )
}

function ProgressTimeline({ deployment }: { deployment: IaCDeployment }) {
    const stages = [
        { key: 'init', label: 'Initialize', statuses: ['planning'] },
        { key: 'plan', label: 'Plan', statuses: ['planned'] },
        { key: 'apply', label: 'Deploy', statuses: ['deploying', 'deployed'] },
    ]

    const statusOrder = ['pending', 'planning', 'planned', 'deploying', 'deployed']
    const currentIdx = statusOrder.indexOf(deployment.status)

    return (
        <div className="flex items-center gap-1 mt-2">
            {stages.map((stage, i) => {
                const stageStartIdx = i === 0 ? 1 : i === 1 ? 2 : 3
                const isComplete = currentIdx > stageStartIdx
                const isActive = currentIdx === stageStartIdx
                const isFailed = deployment.status === 'failed'

                return (
                    <div key={stage.key} className="flex items-center gap-1 flex-1">
                        <div className={`flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-semibold flex-shrink-0
                            ${isComplete ? 'bg-green-500 text-white' : isActive ? (isFailed ? 'bg-red-500 text-white' : 'bg-blue-500 text-white animate-pulse') : 'bg-muted text-muted-foreground'}`}>
                            {isComplete ? '✓' : i + 1}
                        </div>
                        <span className={`text-[10px] ${isActive ? 'font-semibold' : 'text-muted-foreground'}`}>{stage.label}</span>
                        {i < stages.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-1 ${isComplete ? 'bg-green-500' : 'bg-muted'}`} />
                        )}
                    </div>
                )
            })}
        </div>
    )
}

/** The clouds a test deployment can target, and how the engineer signs in to each. */
const CLOUDS: Array<{ key: IaCCloud; label: string; signIn: string; region: string }> = [
    { key: 'aws', label: 'AWS', signIn: 'AWS sign-in (aws sso login)', region: 'eu-west-2' },
    { key: 'azure', label: 'Azure', signIn: 'Azure sign-in (az login)', region: 'uksouth' },
    { key: 'gcp', label: 'Google Cloud', signIn: 'Google sign-in (gcloud auth application-default login)', region: 'europe-west2' },
    { key: 'ibm', label: 'IBM Cloud', signIn: 'IBM Cloud SSO sign-in (ibmcloud login --sso)', region: 'eu-gb' },
]

/** The identity fields per cloud - none of them a secret (backend/src/lib/iac-deploy-target.js checks each). */
const IDENTITY_FIELDS: Record<IaCCloud, Array<{ key: string; label: string; hint: string; placeholder: string; required: boolean }>> = {
    aws: [{ key: 'roleArn', label: 'Deployment role ARN', hint: '(the role deploy.sh takes for an hour)', placeholder: 'arn:aws:iam::123456789012:role/VaultSentinel-IaC-Deploy', required: true }],
    azure: [
        { key: 'tenantId', label: 'Tenant ID', hint: '(Entra directory)', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
        { key: 'subscriptionId', label: 'Subscription ID', hint: '(the recovery subscription)', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
    ],
    gcp: [
        { key: 'projectId', label: 'Project ID', hint: '(the recovery project)', placeholder: 'nw-recovery-01', required: true },
        { key: 'serviceAccount', label: 'Deployment service account', hint: '(impersonated; it has no keys)', placeholder: 'iac-deploy@nw-recovery-01.iam.gserviceaccount.com', required: true },
    ],
    ibm: [
        { key: 'accountId', label: 'Account ID', hint: '(32 characters)', placeholder: '0123456789abcdef0123456789abcdef', required: true },
        { key: 'resourceGroup', label: 'Resource group', hint: '(optional)', placeholder: 'recovery', required: false },
    ],
}

export function DeploymentPanel({ projectId, cloudPlatform, files, mainId, scenario, hierarchyContext, onAskAgentToFix, verified = false, onStatusChange, onDownloaded, busy = false }: DeploymentPanelProps) {
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

    // Detect provider from the platform or files content — must be before state that references it
    const detectedProvider: IaCCloud = (() => {
        if (cloudPlatform === 'aws' || cloudPlatform === 'azure' || cloudPlatform === 'gcp' || cloudPlatform === 'ibm') return cloudPlatform
        const allCode = files.map(f => f.code).join('\n')
        if (allCode.includes('provider "azurerm"') || allCode.includes('azurerm_')) return 'azure'
        if (allCode.includes('provider "google"') || allCode.includes('google_')) return 'gcp'
        if (allCode.includes('provider "ibm"') || /\bibm_(is|pi|tg|dl)_/.test(allCode)) return 'ibm'
        return 'aws'
    })()

    const [showCredentials, setShowCredentials] = useState(false)
    const [selectedProvider, setSelectedProvider] = useState<IaCCloud>(detectedProvider)
    const [showConfirmDestroy, setShowConfirmDestroy] = useState(false)
    const [showLogs, setShowLogs] = useState(false)
    // The identity this project deploys as - never a secret. Region plus the cloud's own fields.
    const [identityForm, setIdentityForm] = useState<Record<string, string>>({})

    const provider = selectedProvider

    const { data: target } = useQuery({
        queryKey: ['iac-deploy-target', projectId, provider],
        queryFn: async () => {
            const res = await iacApi.getDeployTarget(projectId!, provider)
            return (res.data?.data ?? null) as IaCDeployTarget | null
        },
        enabled: isAuthenticated && !!projectId && !!provider,
    })

    // Open the form on what is saved, so the engineer confirms it rather than retyping it.
    useEffect(() => {
        if (!showCredentials) return
        setIdentityForm(target ? { region: target.region, ...target.identity } : {})
    }, [showCredentials, target])

    // Get deployment status
    const { data: deployData } = useQuery({
        queryKey: ['iac-deployment', projectId],
        queryFn: async () => {
            if (!projectId) return null
            const res = await iacApi.getDeployment(projectId)
            const d = res.data as any
            return (d?.data || d) as IaCDeployment | null
        },
        enabled: isAuthenticated && !!projectId,
        refetchInterval: (query) => {
            const status = query.state.data?.status
            // Poll more frequently during active operations
            if (status === 'planning' || status === 'deploying' || status === 'destroying') return 3000
            if (status === 'pending' || status === 'planned') return 5000
            return false
        },
    })

    const deployment = deployData || null
    const identityLabel = target ? (target.identity.roleArn || target.identity.subscriptionId || target.identity.serviceAccount || target.identity.accountId || '') : ''
    const isDeployed = deployment?.status === 'deployed'
    const isFailed = deployment?.status === 'failed'
    const isActive = ['planning', 'deploying', 'destroying', 'pending', 'planned'].includes(deployment?.status || '')
    // A real, meaningful deployment exists (not a no-deployment/error response and
    // not the idle "not_deployed" default) — only then is the full status panel useful.
    const hasRealDeployment = ['pending', 'planning', 'planned', 'deploying', 'deployed', 'destroying', 'destroyed', 'failed'].includes(deployment?.status || '')

    // Report status changes up (used to gate Clara availability on the chat side).
    useEffect(() => {
        onStatusChange?.(deployment?.status || 'not_deployed')
    }, [deployment?.status, onStatusChange])

    // Auto-show logs when deployment fails
    useEffect(() => {
        if (isFailed) setShowLogs(true)
    }, [isFailed])

    // Save the identity (no secret: the server refuses anything that looks like one, and says why).
    const saveIdentityMutation = useMutation({
        mutationFn: () => {
            const { region = '', ...identity } = identityForm
            const fields = IDENTITY_FIELDS[selectedProvider].map((f) => f.key)
            const only = Object.fromEntries(Object.entries(identity).filter(([k, v]) => fields.includes(k) && v.trim()))
            return iacApi.saveDeployTarget(projectId!, selectedProvider, { region: region.trim(), identity: only })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['iac-deploy-target', projectId, selectedProvider] })
        },
        onError: (e: any) => toast({ title: 'The deployment identity was not saved', description: e?.response?.data?.error || e?.message, variant: 'destructive' }),
    })

    // Create deployment record and download smart script
    const createDeployMutation = useMutation({
        mutationFn: async () => {
            const res = await iacApi.createDeployment({ projectId: projectId!, cloudProvider: provider })
            const d = res.data as any
            const dep = d?.data || d
            return dep as IaCDeployment
        },
        onSuccess: (dep) => {
            queryClient.invalidateQueries({ queryKey: ['iac-deployment', projectId] })
            setShowCredentials(false)
            // Download the project with the smart deploy script
            downloadSmartProject(dep)
            toast({ title: 'Deployment initiated', description: 'Extract and run deploy.sh — progress will be reported back here.' })
        },
        onError: (e: any) => toast({ title: 'Failed to create deployment', description: e?.response?.data?.error, variant: 'destructive' }),
    })

    // Update deployment to destroyed
    const destroyMutation = useMutation({
        mutationFn: async () => {
            if (!deployment?.id) throw new Error('No deployment')
            // A fresh token (its progress reports are accepted), the identity and the session name.
            const prepared = (await iacApi.prepareScript(deployment.id)).data.data
            await iacApi.updateDeploymentStatus(deployment.id, { status: 'destroying', statusMessage: 'Destroy initiated — run destroy.sh on your device' })
            return prepared
        },
        onSuccess: (prepared) => {
            queryClient.invalidateQueries({ queryKey: ['iac-deployment', projectId] })
            setShowConfirmDestroy(false)
            downloadDestroyScript(prepared)
            toast({ title: 'Run destroy.sh to remove cloud resources' })
        },
        onError: (e: any) => toast({ title: 'The destroy script could not be prepared', description: e?.response?.data?.error, variant: 'destructive' }),
    })

    const scriptOpts = () => ({ baseUrl: window.location.origin, mainId, generatedAt: new Date().toISOString() })

    async function downloadSmartProject(dep: IaCDeployment) {
        const allFiles = [
            ...files,
            { filename: 'deploy.sh', code: deployScript(dep, scriptOpts()) },
            { filename: 'destroy.sh', code: destroyScript(dep, scriptOpts()) },
        ]
        await downloadAsZip(allFiles, `${mainId}-terraform-project.zip`)
    }

    function downloadDestroyScript(dep: IaCDeployment) {
        const code = destroyScript(dep, scriptOpts())
        const blob = new Blob([code], { type: 'text/plain' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `${mainId}-destroy.sh`
        a.click()
    }

    async function downloadProject() {
        await downloadAsZip(files, `${mainId}-terraform.zip`)
    }

    async function downloadAsZip(fileList: Array<{ filename: string; code: string }>, zipName: string) {
        const zip = new JSZip()
        const folder = zip.folder(mainId) || zip
        for (const f of fileList) {
            folder.file(f.filename, f.code || '')
        }
        const blob = await zip.generateAsync({ type: 'blob' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = zipName
        a.click()
        URL.revokeObjectURL(a.href)
    }

    // Get the error message from the deployment for the "Ask Agent to Fix" button
    function getDeploymentError(): string {
        if (!deployment) return ''
        const parts: string[] = []
        if (deployment.statusMessage) parts.push(deployment.statusMessage)
        // Check if plan or apply output contains error details
        if (deployment.applyOutput && deployment.status === 'failed') {
            // Extract the last 100 lines of output
            const lines = deployment.applyOutput.split('\n')
            parts.push('--- Terraform Output ---')
            parts.push(lines.slice(-100).join('\n'))
        } else if (deployment.planOutput && deployment.status === 'failed') {
            const lines = deployment.planOutput.split('\n')
            parts.push('--- Terraform Output ---')
            parts.push(lines.slice(-100).join('\n'))
        }
        return parts.join('\n\n')
    }

    if (files.length === 0) return null

    return (
        <div className="space-y-3">
            {/* Before any real deployment activity the status pill + files count sit
                inline on the single action row below (keeps the bar one row tall). */}
            {/* Full deployment status panel — shown once a deployment is underway/done. */}
            {deployment && hasRealDeployment && (
                <div className="rounded-lg border bg-muted/30 overflow-hidden">
                    <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3">
                            <StatusBadge status={deployment.status} />
                            {deployment.statusMessage && !isFailed && (
                                <span className="text-xs text-muted-foreground max-w-[300px] truncate">
                                    {deployment.statusMessage}
                                </span>
                            )}
                            {deployment.deployedAt && (
                                <span className="text-xs text-muted-foreground">
                                    Deployed: {formatDateTime(deployment.deployedAt)}
                                </span>
                            )}
                            {deployment.destroyedAt && (
                                <span className="text-xs text-muted-foreground">
                                    Destroyed: {formatDateTime(deployment.destroyedAt)}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Show logs button when there's output */}
                            {(deployment.planOutput || deployment.applyOutput || deployment.statusMessage) && (
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowLogs(!showLogs)}>
                                    <Terminal className="h-3 w-3 mr-1" />
                                    Logs
                                    {showLogs ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                                </Button>
                            )}
                            {isDeployed && (
                                <Button variant="destructive" size="sm" className="h-7" onClick={() => setShowConfirmDestroy(true)}>
                                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />Destroy
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Progress timeline for active deployments */}
                    {isActive && <div className="px-3 pb-3"><ProgressTimeline deployment={deployment} /></div>}

                    {/* Error display */}
                    {isFailed && (
                        <div className="border-t bg-red-50 dark:bg-red-950/30 p-3">
                            <div className="flex items-start gap-2 mb-2">
                                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-red-700 dark:text-red-400">Deployment Failed</p>
                                    <p className="text-xs text-red-600 dark:text-red-400/80 mt-0.5 break-words whitespace-pre-wrap" style={{ maxHeight: '80px', overflow: 'auto' }}>
                                        {deployment.statusMessage || 'Unknown error'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {onAskAgentToFix && (
                                    <Button size="sm" className="h-7 bg-blue-600 hover:bg-blue-700" onClick={() => onAskAgentToFix(getDeploymentError())}>
                                        <Wrench className="h-3.5 w-3.5 mr-1.5" />Ask Agent to Fix
                                    </Button>
                                )}
                                <Button variant="outline" size="sm" className="h-7" onClick={() => {
                                    // Reset and retry
                                    if (deployment?.id) {
                                        iacApi.updateDeploymentStatus(deployment.id, { status: 'not_deployed', statusMessage: 'Reset for retry' })
                                            .then(() => queryClient.invalidateQueries({ queryKey: ['iac-deployment', projectId] }))
                                    }
                                }}>
                                    Retry
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Collapsible logs */}
                    {showLogs && (deployment.planOutput || deployment.applyOutput || deployment.destroyOutput) && (
                        <div className="border-t">
                            <pre className="p-3 text-[10px] font-mono leading-relaxed bg-zinc-950 text-zinc-300 max-h-[200px] overflow-auto whitespace-pre-wrap">
                                {deployment.applyOutput || deployment.planOutput || deployment.destroyOutput || deployment.statusMessage}
                            </pre>
                        </div>
                    )}
                </div>
            )}

            {/* Action / status bar — a single compact row before a real deployment. */}
            <div className="flex items-center gap-2">
                {!hasRealDeployment && (
                    <>
                        <Badge variant="outline" className="text-[9px] text-muted-foreground">{files.length} files generated</Badge>
                        <StatusBadge status="not_deployed" />
                    </>
                )}
                {target && (
                    <Badge variant="outline" className="text-xs text-green-600 max-w-[260px]" title={`Deploys as ${identityLabel} in ${target.region}. No secret is stored: deploy.sh signs you in.`}>
                        <Shield className="h-3 w-3 mr-1 flex-shrink-0" /><span className="truncate">Deploys as {identityLabel}</span>
                    </Badge>
                )}
                <div className="flex-1" />
                {!verified && files.length > 0 && (
                    <Badge variant="outline" className="text-xs text-amber-600">
                        <Clock className="h-3 w-3 mr-1" />Awaiting verification
                    </Badge>
                )}
                {verified && (
                    <Badge variant="outline" className="text-xs text-green-600">
                        <CheckCircle2 className="h-3 w-3 mr-1" />Verified
                    </Badge>
                )}
                {!isDeployed && !isActive && (
                    <Button size="sm" onClick={() => setShowCredentials(true)} disabled={!verified || busy}>
                        <Rocket className="h-3.5 w-3.5 mr-1.5" />Test Deployment
                    </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => { downloadProject(); onDownloaded?.(); }} disabled={files.length === 0 || busy}>
                    <Download className="h-3.5 w-3.5 mr-1.5 text-blue-600" />Download
                </Button>
            </div>

            {/* Deployment identity — which role / subscription / service account / account to deploy as. No secret. */}
            {showCredentials && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-lg mx-4 w-full max-h-[90vh] overflow-y-auto" data-testid="iac-identity-dialog">
                        <h3 className="text-lg font-semibold mb-2">Test Deployment — Deployment Identity</h3>

                        <div className="flex gap-1 mb-4 p-1 rounded-lg bg-muted">
                            {CLOUDS.map((p) => (
                                <button key={p.key}
                                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${selectedProvider === p.key ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                                    onClick={() => setSelectedProvider(p.key)}>
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 mb-4">
                            <Shield className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-emerald-800 dark:text-emerald-300">
                                <strong>No keys or passwords.</strong> deploy.sh signs you in with your own {CLOUDS.find((c) => c.key === selectedProvider)?.signIn} and takes this identity for one hour. VaultSentinel keeps only which identity it is, and the cloud's audit log records you by name.
                            </span>
                        </div>

                        <div className="rounded-lg border bg-blue-50/50 dark:bg-blue-950/20 p-3 mb-4">
                            <p className="text-xs font-medium mb-1">Where these come from</p>
                            <p className="text-xs text-muted-foreground">Run the generated <code className="inline-code">{`setup-${selectedProvider}.sh`}</code> once in the recovery {selectedProvider === 'azure' ? 'subscription' : selectedProvider === 'gcp' ? 'project' : 'account'}. It creates the deployment identity with no keys and prints these values.</p>
                        </div>

                        <div className="space-y-3">
                            {IDENTITY_FIELDS[selectedProvider].map((f) => (
                                <div key={f.key}>
                                    <Label className="text-xs">{f.label} <span className="text-muted-foreground">{f.hint}</span></Label>
                                    <Input value={identityForm[f.key] || ''} onChange={(e) => setIdentityForm((v) => ({ ...v, [f.key]: e.target.value }))}
                                        placeholder={f.placeholder} className="font-mono text-sm" autoComplete="off" spellCheck={false} />
                                </div>
                            ))}
                            <div>
                                <Label className="text-xs">Region <span className="text-muted-foreground">(where the recovery environment is built)</span></Label>
                                <Input value={identityForm.region || ''} onChange={(e) => setIdentityForm((v) => ({ ...v, region: e.target.value }))}
                                    placeholder={CLOUDS.find((c) => c.key === selectedProvider)?.region} className="font-mono text-sm" autoComplete="off" spellCheck={false} />
                            </div>
                        </div>

                        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 mt-3">
                            <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-yellow-700 dark:text-yellow-400">
                                This creates <strong>real cloud resources</strong> that incur costs. Use a recovery or sandbox {selectedProvider === 'azure' ? 'subscription' : selectedProvider === 'gcp' ? 'project' : 'account'}, never production.
                            </span>
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                            <Button variant="outline" size="sm" onClick={() => setShowCredentials(false)}>Cancel</Button>
                            <Button size="sm" onClick={() => {
                                saveIdentityMutation.mutate(undefined, {
                                    onSuccess: () => {
                                        setShowCredentials(false)
                                        createDeployMutation.mutate()
                                    }
                                })
                            }}
                                disabled={!projectId || saveIdentityMutation.isPending || createDeployMutation.isPending || !identityForm.region?.trim()
                                    || IDENTITY_FIELDS[selectedProvider].some((f) => f.required && !identityForm[f.key]?.trim())}
                                data-testid="iac-run-deployment">
                                {(saveIdentityMutation.isPending || createDeployMutation.isPending)
                                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    : <Rocket className="h-3.5 w-3.5 mr-1.5" />}
                                Run Deployment
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Destroy confirmation */}
            {showConfirmDestroy && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-2 text-destructive">Destroy Environment</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                            This will download a destroy script. Run it to remove all cloud resources.
                        </p>
                        <div className="text-sm space-y-1 mb-4 text-xs text-muted-foreground">
                            <p>1. Download destroy.sh</p>
                            <p>2. Run <code className="inline-code">bash destroy.sh</code> in the project directory</p>
                            <p>3. Status will be reported back here</p>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowConfirmDestroy(false)}>Cancel</Button>
                            <Button variant="destructive" size="sm" onClick={() => destroyMutation.mutate()} disabled={destroyMutation.isPending}>
                                <Trash2 className="h-3.5 w-3.5 mr-1.5" />Download Destroy Script
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
